import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { Store } from '@ngrx/store'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import * as fromRoot from '@tv3/store/state'
import { ConnectionService } from '@tv3/store/connection/connection.service'
import {
  ChannelNameTokens,
  Rental,
  RateService,
  RentalRateItem,
  localTodayToUTC,
  isSubunit,
  Toaster,
} from '@tokeet-frontend/tv3-platform'
import { SaveForm } from '@tokeet-frontend/tv3-platform'
import { Observable, of, Subject } from 'rxjs'
import { ChannelRoomRateResponse, GetChannelRoomRatesPayload } from '@tv3/store/connection/connection.types'
import { catchError, distinctUntilChanged, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators'
import * as lodash from 'lodash'
import { OpenRentalOverlay } from '@tv3/store/overlay/overlay.actions'
import { RentalDialogTab } from '@tv3/containers/rentals/overlays/rental-overlay/rental-overlay.component'
import { getRealChannelId } from '@tv3/store/connection/connection.model'

export interface SelectRateToPushDialogConfig {
  title?: string
  help?: string
  buttonIcon?: string
  buttonText?: string
  channelId: string
  channelName: ChannelNameTokens
  channelFriendlyName: string
  propertyId: string | number
  roomId: string | number
  categories: { id: string; name: string }[]
  rental?: Rental
}

@Component({
  selector: 'app-select-rate-to-push-dialog',
  host: { class: 'modal-content' },
  templateUrl: './select-rate-to-push-dialog.component.html',
  styleUrls: ['./select-rate-to-push-dialog.component.scss'],
})
export class SelectRateToPushDialogComponent implements OnInit, OnDestroy {
  config = {
    title: $localize`Select Rate Category`,
    help: $localize`This will push your rental rates from AdvanceCM to the selected channel. The channel rates will be overwritten with your AdvanceCM rates so be certain that your AdvanceCM rates are correct. Please select a AdvanceCM rate category to push from, then a channel rate category to push to.`,
    buttonText: $localize`Push Rate`,
  } as SelectRateToPushDialogConfig

  form = this.fb.group({
    category: ['default', [Validators.required]],
    channelRate: [undefined, [Validators.required]],
  })

  channelRoomRates$: Observable<ChannelRoomRateResponse[]>
  sampleRates: RentalRateItem[] = []
  sampleLoading = false
  sampleFailed = false
  channelAdjusted = false
  private destroyed$ = new Subject<void>()

  constructor(
    public dialogRef: MatDialogRef<SelectRateToPushDialogComponent>,
    private fb: FormBuilder,
    private store: Store<fromRoot.State>,
    private connectionService: ConnectionService,
    private rateService: RateService,
    private toaster: Toaster,
    @Inject(MAT_DIALOG_DATA) public data: { config: SelectRateToPushDialogConfig }
  ) {
    const config = this.data.config
    const payload: GetChannelRoomRatesPayload = {
      api: config.channelName,
      channel_id: config.channelId,
      id: config.roomId,
      propertyId: config.propertyId,
    }

    this.channelRoomRates$ = this.connectionService
      .getRoomRatesFromConnection(this.data.config.channelName, this.data.config.channelId, payload)
      .pipe(tap((items) => this.form.patchValue({ channelRate: this.getDefaultRoomRate(items) })))
  }

  getDefaultRoomRate(rates: ChannelRoomRateResponse[]) {
    return lodash.find(rates, (rate) => lodash.toLower(rate.name).includes('standard')) || lodash.first(rates)
  }

  ngOnInit() {
    this.config = { ...this.config, ...this.data.config }
    if (!this.config.rental) return

    const rental = this.config.rental
    const rentalId = isSubunit(rental) ? rental.multi_unit_id : rental.id
    const channelId = getRealChannelId(this.config.channelId)
    const start = localTodayToUTC()
    const end = start.clone().add(6, 'days')
    this.form
      .get('category')
      .valueChanges.pipe(
        startWith(this.form.get('category').value),
        distinctUntilChanged(),
        switchMap((category: string) => {
          this.sampleLoading = true
          this.sampleFailed = false
          this.sampleRates = []
          this.channelAdjusted = false
          return this.rateService
            .getRates([rentalId], start.unix(), end.unix(), lodash.uniq([category || 'default', 'default', channelId]))
            .pipe(
              map((response) => {
                const categories = response?.items?.find((item) => item.rental_id === rentalId)?.categories || {}
                // RateSyncr gives explicit custom categories precedence over channel adjustments.
                const effective =
                  category && category !== 'default'
                    ? category
                    : categories[channelId]?.length
                    ? channelId
                    : 'default'
                this.channelAdjusted = effective === channelId
                return (categories[effective] || []).slice(0, 7)
              }),
              catchError((error) => {
                this.toaster.error(null, 'Error', error)
                this.sampleFailed = true
                return of([] as RentalRateItem[])
              })
            )
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe((rates) => {
        this.sampleRates = rates
        this.sampleLoading = false
      })
  }

  viewRatesCalendar() {
    this.store.dispatch(OpenRentalOverlay({ rentalId: this.config.rental.id, activeTab: RentalDialogTab.Calendar }))
  }

  ngOnDestroy() {
    this.destroyed$.next()
    this.destroyed$.complete()
  }

  close() {
    this.dialogRef.close()
  }

  @SaveForm()
  save(form: FormGroup) {
    const formValues = this.form.getRawValue()
    this.dialogRef.close(formValues)
  }
}
